import { ufoEventPostOrderAccountOrgList } from "@/components/signup/utils";
import {
    useBxpWebStargateClient,
    GetSignupAccountOrgDetailsResponse,
    GetAccountOrgsRequest,
    UseSignupAccountOrgDetailsRequest,
    UseSignupAccountOrgDetailsResponse
} from "@/libraries/bxp-web-stargate-client/src/index";

import { AsyncResult, useAsync } from "./use-async";

export type {
    GetAccountOrgsRequest,
    GetSignupAccountOrgDetailsResponse,
    UseSignupAccountOrgDetailsRequest,
    UseSignupAccountOrgDetailsResponse
};

export function useSignupAccountOrgDetails({
    cloudSessionTokenCookie,
    skipNames,
    pageSize,
    offeringIds,
    preselectedOrgId
}: UseSignupAccountOrgDetailsRequest): AsyncResult<
    UseSignupAccountOrgDetailsResponse | undefined
> {
    const stargateClient = useBxpWebStargateClient();
    const getOrgDetail = (orgId: string, index: number) => {
        if (pageSize && pageSize > index) {
            return stargateClient.getSignupAccountOrgDetails({
                offeringIds,
                cloudSessionTokenCookie,
                orgId
            });
        }
        return;
    };
    const getPreselectedOrgDetail = async (orgId: string) => {
        const orgDetail = await getOrgDetail(orgId, 0);
        const success = !!(
            orgDetail &&
            orgDetail.success &&
            orgDetail.organization
        );
        if (success) {
            return {
                success: true,
                accountOrgs: {
                    success: true,
                    accountOrgs: [
                        {
                            id: orgId,
                            name: orgDetail.organization.displayName,
                            isAdmin: true
                        }
                    ]
                },
                orgDetails: [
                    {
                        success: true,
                        organization: {
                            id: orgId,
                            displayName: orgDetail.organization.displayName,
                            siteIds: orgDetail.organization.siteIds,
                            entitlementsByOfferingIds:
                                orgDetail.organization.entitlementsByOfferingIds
                        }
                    }
                ]
            };
        } else {
            return {
                success: false
            } as UseSignupAccountOrgDetailsResponse;
        }
    };
    const getOrgDetails = async () => {
        if (preselectedOrgId) {
            return await getPreselectedOrgDetail(preselectedOrgId);
        }
        ufoEventPostOrderAccountOrgList.addCustomData({
            reactComponent: "use-adminhub-org-details"
        });
        const accountOrgs = await stargateClient.getAccountOrgs({
            cloudSessionTokenCookie,
            skipNames
        });
        if (accountOrgs.success) {
            ufoEventPostOrderAccountOrgList.addCustomData({
                accountOrgs: JSON.stringify(accountOrgs.accountOrgs)
            });
            const orgDetails = (
                await Promise.all(
                    accountOrgs.accountOrgs
                        ? accountOrgs.accountOrgs.map((org, index) =>
                              getOrgDetail(org.id, index)
                          )
                        : []
                )
            ).filter((orgDetail) => !!orgDetail);
            ufoEventPostOrderAccountOrgList.addCustomData({
                accountOrgDetailsCount: orgDetails.length
            });
            ufoEventPostOrderAccountOrgList.complete();
            return {
                success:
                    accountOrgs.success &&
                    orgDetails.some((orgDetail) => orgDetail.success),
                accountOrgs,
                orgDetails
            };
        } else {
            ufoEventPostOrderAccountOrgList.error({
                errorName: "accountOrgs",
                errorMessage: "get accountOrgs failed"
            });
            return;
        }
    };

    return useAsync(() => getOrgDetails(), []);
}
